/**
 * Per-date overrides of the weekly opening hours (#2465, split out of the
 * 991-line workshop form).
 *
 * Unlike every other section on the workshop tab these save immediately as
 * their own resource rather than waiting for the page's Save button, and they
 * never touch `settings`. That is why they own their hooks and dialogs here
 * rather than taking them as props — and why the split is safe.
 */

import { useState, useMemo } from 'react';
import { Box, Paper, Typography, Button, IconButton, Chip, Tooltip, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import type { HoursException } from '@/types';
import { HoursExceptionDialog } from '../HoursExceptionDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useHoursExceptions,
  useCreateHoursException,
  useUpdateHoursException,
  useDeleteHoursException,
} from '@/hooks/useHoursExceptions';
import { useNotify } from '@/hooks/useNotify';
import { intlLocale } from '@/utils/locale';

export function WorkshopSectionHoursExceptions() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const { exceptions: hoursExceptions } = useHoursExceptions();
  const createException = useCreateHoursException();
  const updateException = useUpdateHoursException();
  const deleteException = useDeleteHoursException();
  const notify = useNotify();

  // Exception dialog state
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [editingException, setEditingException] = useState<HoursException | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingException, setDeletingException] = useState<HoursException | null>(null);
  const [showPast, setShowPast] = useState(false);

  const handleAddException = () => {
    setEditingException(null);
    setExceptionDialogOpen(true);
  };

  const handleEditException = (exception: HoursException) => {
    setEditingException(exception);
    setExceptionDialogOpen(true);
  };

  const handleSaveException = (exception: HoursException) => {
    const isExisting = hoursExceptions.some((e) => e.id === exception.id);
    const payload = {
      date: exception.date,
      isClosed: exception.isClosed,
      // A closed day must not carry times — the API rejects the pair.
      ...(exception.isClosed
        ? {}
        : {
            ...(exception.open !== undefined ? { open: exception.open } : {}),
            ...(exception.close !== undefined ? { close: exception.close } : {}),
          }),
      ...(exception.reason !== undefined && exception.reason !== ''
        ? { reason: exception.reason }
        : {}),
    };

    const onDone = () => {
      setExceptionDialogOpen(false);
      setEditingException(null);
    };
    const onFail = (error: unknown) => {
      const status = (error as { status?: number }).status;
      notify.error(
        tc('error'),
        status === 409 ? t('hoursExceptionDuplicate') : t('hoursExceptionSaveFailed'),
      );
    };

    if (isExisting) {
      updateException.mutate(
        { id: exception.id, ...payload },
        { onSuccess: onDone, onError: onFail },
      );
    } else {
      createException.mutate(payload, { onSuccess: onDone, onError: onFail });
    }
  };

  const handleDeleteClick = (exception: HoursException) => {
    setDeletingException(exception);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deletingException) return;
    deleteException.mutate(deletingException.id, {
      onSuccess: () => {
        setDeleteConfirmOpen(false);
        setDeletingException(null);
      },
      onError: () => {
        notify.error(tc('error'), t('hoursExceptionDeleteFailed'));
      },
    });
  };

  // Split exceptions into future/today and past
  const today = new Date().toISOString().slice(0, 10);
  const { futureExceptions, pastExceptions } = useMemo(() => {
    const all = [...hoursExceptions].sort((a, b) => a.date.localeCompare(b.date));
    return {
      futureExceptions: all.filter((e) => e.date >= today),
      pastExceptions: all.filter((e) => e.date < today),
    };
  }, [hoursExceptions, today]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(intlLocale(), {
      weekday: 'short',
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  };

  const renderExceptionRow = (exception: HoursException, isPast: boolean) => (
    <Box
      key={exception.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 1,
        opacity: isPast ? 0.5 : 1,
        '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
      }}
    >
      <Typography variant="body2" sx={{ minWidth: 130, fontWeight: 500 }}>
        {formatDate(exception.date)}
      </Typography>

      {exception.isClosed ? (
        <Chip
          label={t('closedAllDay')}
          size="small"
          color="default"
          variant="outlined"
        />
      ) : (
        <Chip
          label={`${exception.open ?? ''} - ${exception.close ?? ''}`}
          size="small"
          color="info"
          variant="outlined"
        />
      )}

      {exception.reason && (
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} noWrap>
          {exception.reason}
        </Typography>
      )}

      <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title={tc('edit')}>
          <IconButton size="small" onClick={() => handleEditException(exception)} aria-label={tc('edit')}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={tc('delete')}>
          <IconButton size="small" onClick={() => handleDeleteClick(exception)} aria-label={tc('delete')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Hours Exceptions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('hoursExceptions')}
          </Typography>
          {/*
            Now that exceptions sit directly below the weekly hours (#2467),
            the two save differently on one screen: these persist on add, the
            hours above wait for the page's Save button. Unmarked, a workshop
            could reasonably read the opposite and lose the hours it just
            edited, so the difference is labelled rather than left implicit.
          */}
          <Tooltip title={t('hoursExceptionsSavedImmediatelyHint')}>
            <Chip label={t('hoursExceptionsSavedImmediately')} size="small" variant="outlined" />
          </Tooltip>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddException}
        >
          {t('addException')}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('hoursExceptionsDesc')}
      </Typography>

      {/*
        mb: 4 like every other workshop section's Paper (#2709). Sections carry
        their own trailing gap rather than the next heading carrying a leading
        one, so the one Paper that omitted it left the following heading
        ("Aikataulutus") touching this box.
      */}
      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', mb: 4 }}>
        {futureExceptions.length === 0 && pastExceptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {t('noExceptions')}
          </Typography>
        ) : (
          <Box>
            {futureExceptions.map((e) => renderExceptionRow(e, false))}

            {pastExceptions.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Button
                  size="small"
                  onClick={() => setShowPast(!showPast)}
                  sx={{ mb: showPast ? 1 : 0 }}
                >
                  {showPast
                    ? t('hidePast')
                    : `${t('showPast')} (${String(pastExceptions.length)})`}
                </Button>
                {showPast && pastExceptions.map((e) => renderExceptionRow(e, true))}
              </>
            )}
          </Box>
        )}
      </Paper>

      {/* Exception Dialog */}
      <HoursExceptionDialog
        open={exceptionDialogOpen}
        onClose={() => {
          setExceptionDialogOpen(false);
          setEditingException(null);
        }}
        onSave={handleSaveException}
        exception={editingException}
        isSaving={createException.isPending || updateException.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        title={tc('delete')}
        message={t('deleteExceptionConfirm', {
          date: deletingException ? formatDate(deletingException.date) : '',
        })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setDeletingException(null);
        }}
      />
    </>
  );
}
